import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '@/contexts/LanguageContext';
import { buildYouTubeEmbedUrl, sendYouTubeCommand } from '@/utils/youtube';

interface YouTubeShortCardProps {
  videoId: string;
  activityLabel: string;
  cardIndex: number;
  isMobile: boolean;
  onRegisterIframe: (index: number, iframe: HTMLIFrameElement | null) => void;
  onRequestPlay: (index: number) => void;
  onRequestPause: (index: number) => void;
}

const YouTubeShortCard: React.FC<YouTubeShortCardProps> = ({
  videoId,
  activityLabel,
  cardIndex,
  isMobile,
  onRegisterIframe,
  onRequestPlay,
  onRequestPause,
}) => {
  const { t } = useLanguage();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [iframeReady, setIframeReady] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);

  const { ref: containerRef, inView } = useInView({
    threshold: 0.5,
    rootMargin: '100px 0px',
    triggerOnce: false,
  });

  useEffect(() => {
    if (inView) {
      setShouldLoad(true);
    }
  }, [inView]);

  useEffect(() => {
    onRegisterIframe(cardIndex, iframeRef.current);
    return () => onRegisterIframe(cardIndex, null);
  }, [cardIndex, onRegisterIframe, iframeReady]);

  const play = useCallback(() => {
    onRequestPlay(cardIndex);
  }, [cardIndex, onRequestPlay]);

  const pause = useCallback(() => {
    onRequestPause(cardIndex);
  }, [cardIndex, onRequestPause]);

  useEffect(() => {
    if (!iframeReady || !shouldLoad) {
      return;
    }

    if (isMobile) {
      if (inView) {
        play();
      } else {
        pause();
      }
    }
  }, [inView, isMobile, iframeReady, shouldLoad, play, pause]);

  const handleMouseEnter = () => {
    if (!isMobile && iframeReady) {
      play();
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && iframeReady) {
      pause();
    }
  };

  const toggleMute = (event: React.MouseEvent) => {
    event.stopPropagation();
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    if (isMuted) {
      sendYouTubeCommand(iframe, 'unMute');
      setIsMuted(false);
    } else {
      sendYouTubeCommand(iframe, 'mute');
      setIsMuted(true);
    }
  };

  const handleIframeLoad = () => {
    setIframeReady(true);
    onRegisterIframe(cardIndex, iframeRef.current);
    sendYouTubeCommand(iframeRef.current, 'mute');
  };

  return (
    <div
      ref={containerRef}
      className="w-full max-w-sm mx-auto"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative bg-white rounded-2xl shadow-md overflow-hidden aspect-[9/16]">
        {shouldLoad ? (
          <iframe
            ref={iframeRef}
            src={buildYouTubeEmbedUrl(videoId)}
            title={`${activityLabel} YouTube Short ${cardIndex + 1}`}
            className="absolute inset-0 w-full h-full border-0"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            onLoad={handleIframeLoad}
          />
        ) : (
          <div className="absolute inset-0 bg-gray-200 animate-pulse" aria-hidden="true" />
        )}

        {shouldLoad && (
          <button
            type="button"
            onClick={toggleMute}
            className="absolute bottom-3 right-3 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
            aria-label={isMuted ? t('unmute') : t('mute')}
          >
            {isMuted ? (
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            ) : (
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default YouTubeShortCard;
